/**
 * Shared formatters for the selection-card sidebar. All of these expect
 * GraphQL-wire values (Decimal128 strings, ISO date strings, enum-style
 * UPPERCASE_SNAKE strings). They never mutate the input — they just format
 * it for display.
 *
 * See docs/admin/context-bundle-rendering.md §4 for the field-format quirks
 * that drove each helper.
 */

// Money fields land as Decimal128 strings ("297.00", "1.23456789e8", etc.).
// Parsing into a Number is safe for *display only* — never use the parsed
// value for arithmetic on large amounts.
export const formatMoney = (rawAmount, currency, opts = {}) => {
  if (rawAmount === null || rawAmount === undefined || rawAmount === '') {
    return '—';
  }
  const num = typeof rawAmount === 'number' ? rawAmount : Number(rawAmount);
  if (Number.isNaN(num)) return String(rawAmount);
  const minimumFractionDigits = opts.minimumFractionDigits ?? 2;
  const maximumFractionDigits = opts.maximumFractionDigits ?? 2;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(num);
  return currency ? `${formatted} ${currency}` : formatted;
};

export const formatDateAbsolute = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTimeAbsolute = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Show relative for ≤ 30 days, absolute otherwise. The 30-day cutoff matches
// the spec: "render relative time for recency and absolute for 'older than
// 30 days'."
const DAY_MS = 86400000;
export const formatDateSmart = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / DAY_MS);
  if (days > 30 || days < -30) return formatDateAbsolute(iso);
  if (days >= 1) return `${days}d ago`;
  const hours = Math.floor(diff / 3600000);
  if (hours >= 1) return `${hours}h ago`;
  const minutes = Math.floor(diff / 60000);
  if (minutes >= 1) return `${minutes}m ago`;
  if (minutes <= -1) return `in ${-minutes}m`;
  return 'just now';
};

// UPPERCASE_SNAKE / camelCase → "Upper Snake" / "Camel Case". Safe for emails,
// URLs, ISO timestamps, and numeric strings (they pass through untouched).
export const humanize = input => {
  if (input === null || input === undefined || input === '') return '—';
  const str = String(input);
  if (/[@:/]/.test(str)) return str;
  if (/^\d+(\.\d+)?$/.test(str)) return str;
  return str
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
};

// Status pill tone. Green for healthy, amber for in-flight, red for failed.
// Returns one of: 'success' | 'pending' | 'danger' | 'neutral'.
// CONDITIONALLY_APPROVED is spelled out rather than relying on the APPROVED
// alternative: these are anchored, so the longer value would fall to neutral.
const SUCCESS_RX =
  /^(SUCCESS|SUCCESSFUL|APPROVED|CONDITIONALLY_APPROVED|ACTIVE|FILLED|SETTLED|PAID|ENABLED|READ|COMPLETED|DONE)$/i;
const PENDING_RX =
  /^(PENDING|EXPIRING_SOON|IN_PROGRESS|OPEN|PROCESSING|INITIATED|UNPAID|UNREAD|DRAFT|EXPIRING)$/i;
const DANGER_RX =
  /^(FAILED|REJECTED|CANCELLED|CANCELED|EXPIRED|DISABLED|INACTIVE|ARCHIVED|ENDED|DECLINED)$/i;

export const statusTone = status => {
  if (!status) return 'neutral';
  const s = String(status).trim();
  if (SUCCESS_RX.test(s)) return 'success';
  if (PENDING_RX.test(s)) return 'pending';
  if (DANGER_RX.test(s)) return 'danger';
  return 'neutral';
};

export const shortenId = (id, head = 4, tail = 4) => {
  if (!id) return '—';
  const value = String(id);
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
};
